<div class="flex h-screen flex-auto flex-col overflow-hidden">
  <aura-impersonate-banner />

  <!-- Header -->
  <header class="surface-default flex h-16 shrink-0 items-center border-b px-4 sm:px-6 py-1">
    <!-- Brand Logo -->
    @if (isHomePage()) {
      <div class="flex items-center cursor-default select-none shrink-0">
        @if (logoUrl()) {
          <img class="size-7" [src]="logoUrl()" [alt]="appName() || 'Logo'" />
        }
        @if (appName()) {
          <div class="ml-2.5 text-xl sm:text-2xl font-bold tracking-tight">{{ appName() }}</div>
        }
        @if (effectiveBadge(); as badge) {
          <span
            class="ml-2 rounded-md border border-primary-5 bg-primary-a3 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-primary-11"
          >
            {{ badge }}
          </span>
        }
      </div>
    } @else {
      <a [routerLink]="effectiveHomeUrl()" class="flex items-center cursor-pointer select-none shrink-0">
        @if (logoUrl()) {
          <img class="size-7" [src]="logoUrl()" [alt]="appName() || 'Logo'" />
        }
        @if (appName()) {
          <div class="ml-2.5 text-xl sm:text-2xl font-bold tracking-tight">{{ appName() }}</div>
        }
        @if (effectiveBadge(); as badge) {
          <span
            class="ml-2 rounded-md border border-primary-5 bg-primary-a3 px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-primary-11"
          >
            {{ badge }}
          </span>
        }
      </a>
    }

    <!-- Public Audience Switcher (Generic via audienceOptions input) -->
    @if (audienceOptions().length > 0) {
      <div
        role="group"
        aria-label="Choisir votre espace"
        class="ml-3 sm:ml-6 inline-flex items-center rounded-lg border border-neutral-4 bg-neutral-2 p-1 gap-1 shrink-0"
      >
        @for (opt of audienceOptions(); track opt.audience) {
          @let isActive = publicAudience() === opt.audience;
          <button
            type="button"
            (click)="switchPublicAudience(opt)"
            [attr.aria-pressed]="isActive"
            class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors cursor-pointer"
            [class]="
              isActive
                ? 'bg-primary-9 text-white font-semibold shadow-2xs'
                : 'text-neutral-11 hover:text-neutral-12 hover:bg-neutral-3/60 font-medium'
            "
          >
            <mat-icon
              [svgIcon]="opt.icon"
              class="size-3.5!"
              [class]="isActive ? 'text-white!' : 'text-neutral-10!'"
            />
            @if (opt.shortLabel) {
              <span class="hidden md:inline">{{ opt.label }}</span>
              <span class="md:hidden">{{ opt.shortLabel }}</span>
            } @else {
              <span>{{ opt.label }}</span>
            }
          </button>
        }
      </div>
    }

    <!-- Desktop navigation menu -->
    <nav class="ml-6 hidden items-center gap-x-1 lg:flex" aria-label="Navigation principale">
      @for (item of navigationItems(); track item.id) {
        <a
          matButton
          class="tertiary text-sm"
          [routerLink]="item.link"
          [queryParams]="item.queryParams"
          [routerLinkActive]="['bg-neutral-a4']"
          [routerLinkActiveOptions]="{ exact: true }"
        >
          {{ item.label }}
        </a>
      }
    </nav>

    <!-- Right-side actions -->
    <div class="ml-auto flex items-center gap-x-2 pl-3">
      <aura-profile-app-switcher [user]="user()" />
      @if (user(); as user) {
        <aura-notifications-dropdown
          [notifications]="notifications()"
          [unreadCount]="unreadCount()"
          (markAsRead)="markAsRead($event)"
          (markAllAsRead)="markAllAsRead()"
          (viewAll)="goToNotifications()"
        />
        <aura-discussions-button />
        <aura-sidebar-user [user]="user" [scheme]="theming.scheme()" (schemeChanged)="updateScheme($event)" />
        <!-- Mobile navigation menu when authenticated -->
        <button
          matIconButton
          class="lg:hidden"
          [matMenuTriggerFor]="mobileAuthNavMenu"
          aria-label="Ouvrir le menu de navigation"
        >
          <mat-icon svgIcon="menu" />
        </button>
        <mat-menu #mobileAuthNavMenu="matMenu">
          @for (item of navigationItems(); track item.id) {
            <a
              mat-menu-item
              [routerLink]="item.link"
              [queryParams]="item.queryParams"
              [routerLinkActive]="['bg-primary', 'text-primary-contrast']"
              [routerLinkActiveOptions]="{ exact: true }"
            >
              <span>{{ item.label }}</span>
            </a>
          }
        </mat-menu>
      } @else {
        @if (isPublicApp()) {
          <div class="hidden sm:flex items-center gap-x-2">
            <a matButton class="tertiary" routerLink="/auth/sign-in">
              <span>Se connecter</span>
            </a>
            @if (activeAudienceOption(); as activeOpt) {
              @if (activeOpt.ctaHref) {
                <a matButton class="primary" [href]="activeOpt.ctaHref">
                  <span>{{ activeOpt.ctaLabel }}</span>
                </a>
              } @else if (activeOpt.ctaRouterLink) {
                <a matButton class="primary" [routerLink]="activeOpt.ctaRouterLink">
                  <span>{{ activeOpt.ctaLabel }}</span>
                </a>
              }
            }
          </div>
        }

        <button
          matIconButton
          class="lg:hidden"
          [matMenuTriggerFor]="guestMobileMenu"
          aria-label="Ouvrir le menu"
        >
          <mat-icon svgIcon="menu" />
        </button>

        <mat-menu #guestMobileMenu="matMenu" class="min-w-56!">
          @for (item of navigationItems(); track item.id) {
            <a
              mat-menu-item
              [routerLink]="item.link"
              [queryParams]="item.queryParams"
              [routerLinkActive]="['bg-primary', 'text-primary-contrast']"
              [routerLinkActiveOptions]="{ exact: true }"
            >
              <span>{{ item.label }}</span>
            </a>
          }
          <mat-divider class="my-1" />
          @if (activeAudienceOption(); as activeOpt) {
            @if (activeOpt.ctaHref) {
              <a mat-menu-item [href]="activeOpt.ctaHref">
                <mat-icon [svgIcon]="activeOpt.ctaIcon" />
                <span class="font-semibold">{{ activeOpt.ctaLabel }}</span>
              </a>
            } @else if (activeOpt.ctaRouterLink) {
              <a mat-menu-item [routerLink]="activeOpt.ctaRouterLink">
                <mat-icon [svgIcon]="activeOpt.ctaIcon" />
                <span class="font-semibold">{{ activeOpt.ctaLabel }}</span>
              </a>
            }
          }
          <a mat-menu-item routerLink="/auth/sign-in">
            <span>Se connecter ou s'inscrire</span>
          </a>
        </mat-menu>
      }
    </div>
  </header>

  <!-- Scrollable Content -->
  <div #contentContainer class="flex flex-1 min-h-0 flex-col overflow-y-auto">
    <router-outlet />
  </div>
</div>
